import React, { useMemo, useRef } from 'react'
import { Box, Stack, useTheme, Skeleton } from '@mui/material'
import { StyledWidget, StyledWidgetLabel } from './WidgetsStyles'
import { useTranslation } from 'react-i18next'
import { PieChart, Pie, Cell, Sector, Tooltip as RechartsTooltip } from 'recharts'

interface WidgetPieChartProps {
  data: {
    name: string;
    value: number;
    color: string;
    tooltip: string;
    filter: string;
    onClick?: (filter: string, event: React.MouseEvent) => void
  }[]
  size: number
  positionTooltip?: {
    x?: number
    y?: number
  }
}

const WidgetPieChart = ({ data, size, positionTooltip = {} }: WidgetPieChartProps) => {
  const [activeIndex, setActiveIndex] = React.useState<number | undefined>(undefined);
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cursor = useMemo(() => {
    return data.some(entry => entry.onClick) ? 'pointer' : 'default'
  }, [data])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onPieEnter = (_: any, index: number) => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    leaveTimeoutRef.current = setTimeout(() => {
      setActiveIndex(undefined);
      leaveTimeoutRef.current = null;
    }, 20);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderActiveShape = (props: any) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;

    return (
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 5}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          backgroundColor: 'grey',
          color: 'white',
          padding: '4px 10px',
          borderRadius: '4px',
          fontSize: '12px',
          width: '230px',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          pointerEvents: 'none'
        }}>
          {data.tooltip}
        </div>
      );
    }
    return null;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePieClick = (entry: any) => {
    if (entry.onClick) {
      entry.onClick(entry.filter, {} as React.MouseEvent);
    }
  };

  return (
    <Box
      sx={{
        width: size,
        height: size,
        '& .recharts-sector': {
          cursor,
        },
      }}
    >
      <PieChart width={size} height={size}>
        <Pie
          activeIndex={activeIndex}
          activeShape={renderActiveShape}
          data={data}
          cx={size / 2.5}
          cy={size / 2.5}
          innerRadius={size / 7}
          outerRadius={size / 2.5}
          paddingAngle={2}
          dataKey="value"
          onMouseEnter={onPieEnter}
          onMouseLeave={onPieLeave}
          style={{ outline: 'none' }}
          onClick={handlePieClick}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.color}
            />
          ))}
        </Pie>
        <RechartsTooltip
          content={<CustomTooltip />}
          wrapperStyle={{
            zIndex: 2,
            pointerEvents: 'none'
          }}
          position={{ y: -20, ...positionTooltip }}
        />
      </PieChart>
    </Box>
  )
}

interface WidgetGenericStatisticProps {
  loading: boolean
  title: string
  positionTooltip?: {
    x?: number
    y?: number
  }
  nbWarning?: number
  nbSuccess?: number
  nbError?: number
  total?: number
  icon?: string
  iconColor?: string
  iconSize?: string
  borderColor?: string
  onClick?: (filter: string) => void
  sx?: Record<string, string>
  cursor?: string
}

const WidgetGenericStatistic = ({
  loading,
  title,
  positionTooltip,
  nbWarning = 0,
  nbSuccess = 0,
  nbError = 0,
  total = 0,
  icon,
  iconColor,
  iconSize,
  borderColor,
  onClick,
  cursor = 'pointer',
  sx = {}
}: WidgetGenericStatisticProps) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const totalFormatted = useMemo(() => {
    if (isNaN(total) || total === null || total === undefined) return '0'
    return new Intl.NumberFormat().format(total)
  }, [total])

  const nbFormatted = useMemo(() => {
    if (isNaN(nbSuccess) || nbSuccess === null || nbSuccess === undefined) return '0'
    return nbSuccess === 0
      ? new Intl.NumberFormat('en-US', {
        minimumIntegerDigits: 1,
        useGrouping: true,
      }).format(nbSuccess)
      : new Intl.NumberFormat().format(nbSuccess)
  }, [nbSuccess])

  const displayWidget = loading || total !== 0
  const primaryWidget = useMemo(() => {
    const validNbSuccess = isNaN(nbSuccess) ? 0 : nbSuccess
    const validTotal = isNaN(total) ? 0 : total
    return validNbSuccess !== validTotal && validNbSuccess !== 0
  }, [nbSuccess, nbWarning, nbError, total])

  const calculatedBorderColor = useMemo(() => {
    const validNbSuccess = isNaN(nbSuccess) ? 0 : nbSuccess
    if (loading) return theme.palette.grey[100]
    if (borderColor) return borderColor
    if (primaryWidget) return theme.palette.warning.main
    if (validNbSuccess === 0) return theme.palette.error.main
    return theme.palette.grey[200]
  }, [loading, primaryWidget, total, theme.palette, borderColor, nbSuccess])

  const statusIcon = useMemo(() => {
    const validNbSuccess = isNaN(nbSuccess) ? 0 : nbSuccess
    if (validNbSuccess === 0) return 'fa-solid fa-xmark'
    return 'fa-solid fa-check'
  }, [nbSuccess])

  const statusIconColor = useMemo(() => {
    const validNbSuccess = isNaN(nbSuccess) ? 0 : nbSuccess
    if (validNbSuccess === 0) return theme.palette.error.main
    return theme.palette.success.main
  }, [nbSuccess, theme.palette])

  const pieChartData = useMemo(() => {
    const validNbSuccess = isNaN(nbSuccess) ? 0 : nbSuccess
    const validNbWarning = isNaN(nbWarning) ? 0 : nbWarning
    const validNbError = isNaN(nbError) ? 0 : nbError

    return [
      {
        name: 'Success',
        value: validNbSuccess,
        color: theme.palette.success.main,
        tooltip: t('kpis_widgets.kpis_lines_informations.operational_device', { number: validNbSuccess, count: validNbSuccess }),
        filter: 'up',
        onClick: onClick ? (filter: string) => onClick(filter) : undefined
      },
      {
        name: 'Warning',
        value: validNbWarning,
        color: theme.palette.warning.main,
        tooltip: t('kpis_widgets.kpis_lines_informations.unreachable_device', { number: validNbWarning, count: validNbWarning }),
        filter: 'down',
        onClick: onClick ? (filter: string) => onClick(filter) : undefined
      },
      {
        name: 'Error',
        value: validNbError,
        color: theme.palette.error.main,
        tooltip: t('kpis_widgets.kpis_lines_informations.error_device', { number: validNbError, count: validNbError }),
        filter: 'error',
        onClick: onClick ? (filter: string) => onClick(filter) : undefined
      },
    ]
  }, [nbSuccess, nbWarning, nbError, theme.palette, t, onClick])

  if (!displayWidget) return null

  return (
    <Box
      sx={{
        borderRadius: '24px',
        cursor,
        ...sx
      }}
    >
      <StyledWidget
        sx={{
          padding: '12px 16px',
          borderRadius: '24px',
          overflow: 'visible',
          cursor,
          border: `1px solid ${calculatedBorderColor}`,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'stretch',
          gap: '10px',
          minHeight: '100px',
        }}
      >
        {loading ? (
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Skeleton
                variant="rectangular"
                width={20}
                height={20}
                animation="wave"
                sx={{ borderRadius: 1, bgcolor: '#EFEFEF' }}
              />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={20}
                animation="wave"
                sx={{ borderRadius: 1, bgcolor: '#EFEFEF' }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                <Skeleton
                  variant="rectangular"
                  width={60}
                  height={36}
                  animation="wave"
                  sx={{ borderRadius: 1, bgcolor: '#EFEFEF' }}
                />
                <Skeleton
                  variant="rectangular"
                  width={25}
                  height={25}
                  animation="wave"
                  sx={{ borderRadius: 1, bgcolor: '#EFEFEF' }}
                />
              </Box>
              <Skeleton
                variant="rectangular"
                width={25}
                height={25}
                animation="wave"
                sx={{ borderRadius: 1, bgcolor: '#EFEFEF' }}
              />
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              justifyContent: 'center',
              flex: primaryWidget ? '0 0 69%' : 1
            }} onClick={onClick ? () => onClick('') : () => { }}>
              {
                icon ? (
                  <StyledWidgetLabel>
                    <Box
                      component="span"
                      sx={{
                        color: iconColor || theme.palette.primary.main,
                        fontSize: iconSize || '16px',
                        marginRight: '8px',
                      }}
                    >
                      <i className={`fa ${icon}`} />
                    </Box>
                    {title}
                  </StyledWidgetLabel>
                ) : (
                  <StyledWidgetLabel>
                    {title}
                  </StyledWidgetLabel>
                )
              }
              <Stack
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  spacing={1}
                >
                  <Box
                    sx={{
                      color: '#5D5D5D',
                      fontSize: '27px',
                      fontWeight: '200',
                      lineHeight: 0.8,
                    }}
                  >
                    {nbFormatted}
                  </Box>
                  <Box
                    sx={{
                      color: '#888888',
                      fontSize: '12px',
                      marginBottom: '3px',
                    }}
                  >
                    / {totalFormatted}
                  </Box>
                </Stack>

                {!primaryWidget && (
                  <Box
                    sx={{
                      color: statusIconColor,
                      fontSize: '24px',
                    }}
                  >
                    <i className={statusIcon} />
                  </Box>
                )}
              </Stack>
            </Box>

            {primaryWidget && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WidgetPieChart
                  positionTooltip={positionTooltip}
                  data={pieChartData}
                  size={70}
                />
              </Box>
            )}
          </>
        )}
      </StyledWidget>
    </Box>
  )
}

export default WidgetGenericStatistic
